import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Table from 'reactstrap/lib/Table';
import { defaultTo, get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildTypeRankingsPath, TypeRankingsModel } from 'client/data/models/type-rankings';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  compareStrings,
  compareValues,
  getTablePropsFromContent,
} from 'site-modules/shared/components/editorial/article-table/article-table-helpers';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ShareThisChart } from 'client/site-modules/shared/components/share-this-chart/share-this-chart';

import 'client/site-modules/shared/components/editorial/article-table/article-table.scss';
import { bootstrapMigrationLeftRightToStartEnd } from 'client/utils/bootstrap/map-align';

const POSSIBLE_ALIGN = ['left', 'right', 'center'];

export const SORT_ORDER = {
  NONE: 'none',
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
};

function transformToRowValues(data) {
  return data
    .reduce((acc, cur) => {
      const { segmentRatings } = cur;
      segmentRatings.forEach(segmentRating => {
        const { modelYear, rating, modelDisplayOverride, priceRange } = segmentRating;
        const name = `${modelYear.makeName} ${modelDisplayOverride || modelYear.modelName}`;
        const href = `/${modelYear.makeSlug}/${modelYear.modelSlug}/`;
        const anchor = `<a class="medium text-primary-darker d-flex align-items-center" href=${href}>${name}</a>`;
        const msrp = getPriceString(get(priceRange, 'min'));
        const rawRatings = rating && Number(`${Math.round(`${rating}e1`)}e-1`);
        const ratings = `<strong>${rawRatings || 'N/A'}</strong>`;
        const rawRange = get(segmentRating, 'edmundsElectricityRange', '');
        const range = rawRange ? `${rawRange} miles` : 'not avail.';
        acc.push({
          name,
          value: [anchor, msrp, ratings, range],
        });
      });
      return acc;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(obj => obj.value);
}

function EvRangeTableUI({
  entry = DEFAULT_CONTENT,
  className = null,
  shareOptions = {},
  vehicleEditorialRankings = [],
}) {
  const {
    title,
    TitleTag,
    columnCategoriesBackgrounds,
    hasCategories,
    columnHeaderBackgrounds,
    fontSize,
    noVerticalBorders,
    isStriped,
    isSmall,
    columnsAlign,
    headersAlign,
    hasVerticalAlignment,
    categoriesStartIndexes,
    columnCategories,
    verticalAlign,
  } = getTablePropsFromContent(entry);

  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [tableSize, setTableSize] = useState({});
  const tableRef = useRef();

  useEffect(() => {
    setTableSize({
      height: +get(tableRef, 'current.clientHeight', 0),
      width: +get(tableRef, 'current.clientWidth', 0),
    });
  }, []);

  function onColumnSort(event) {
    const columnIndex = parseInt(event.currentTarget.dataset.index, 10);

    if (columnIndex === sortColumn) {
      setSortOrder(sortOrder === SORT_ORDER.ASCENDING ? SORT_ORDER.DESCENDING : SORT_ORDER.ASCENDING);
      vehicleEditorialRankings.reverse();
      return;
    }

    setSortColumn(columnIndex);
    setSortOrder(SORT_ORDER.ASCENDING);

    const compare = columnIndex ? compareValues : compareStrings;
    vehicleEditorialRankings.sort((a, b) => compare(a[columnIndex], b[columnIndex]));
  }

  const { canonical, pageTitle, contentId } = shareOptions;
  const entryPath = defaultTo(shareOptions.entryPath, []);

  const COLUMN_WIDTHS = ['40%', '20%', '20%', '20%'];
  const columnCount = COLUMN_WIDTHS.length;

  return (
    <article ref={tableRef}>
      <ShareThisChart
        pageTitle={pageTitle}
        pageUrl={canonical}
        shareText="Share this table"
        shareableWidgetData={{
          backLink: canonical,
          defaultTitle: pageTitle,
          widgetName: 'tables',
          queryParams: { contentId, entryPath: entryPath.join(',') },
          defaultWidth: tableSize.width,
          defaultHeight: tableSize.height,
        }}
        className="text-primary-darker text-end ms-auto"
      />
      <section style={{ fontSize }} className={className}>
        {!!title && <TitleTag className="heading-3 mb-0_5">{title}</TitleTag>}
        <Table
          responsive
          striped={isStriped}
          bordered={!noVerticalBorders && !hasCategories}
          size={isSmall ? 'sm' : undefined}
          className={classnames('article-table', { 'has-categories': hasCategories })}
        >
          <colgroup>
            {COLUMN_WIDTHS.map((width, index) => {
              const key = `${entry.id}-colgroup-${index}`;

              return <col key={key} width={width} />;
            })}
          </colgroup>
          <thead>
            {hasCategories && (
              <tr>
                {columnCategories.map(({ value, colspan }, index) => {
                  const key = `${entry.id}-category-${index}`;
                  return (
                    <th
                      key={key}
                      colSpan={colspan}
                      className={classnames('text-center align-middle', { 'category-border': !!index })}
                      {...(columnCategoriesBackgrounds[index]
                        ? {
                            style: { background: columnCategoriesBackgrounds[index], padding: isSmall ? '0.3rem' : '' },
                          }
                        : { style: { padding: isSmall ? '0.3rem' : '' } })}
                    >
                      <ContentFragment>{value}</ContentFragment>
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {new Array(columnCount).fill().map((value, index) => {
                const key = `${entry.id}-header-${index}`;
                const hasAlignment = POSSIBLE_ALIGN.includes(headersAlign[index]);
                const columnHeaderValue = entry.metadata(`columnHeader${index + 1}`).value();
                const headerText = <ContentFragment>{columnHeaderValue}</ContentFragment>;
                const defaultSortOrder = SORT_ORDER.NONE;
                const hasCategoryBorder = !!index && categoriesStartIndexes.includes(index);

                return (
                  <th
                    scope="col"
                    className={classnames(
                      'align-middle',
                      `text-${hasAlignment ? bootstrapMigrationLeftRightToStartEnd(headersAlign[index]) : 'center'}`,
                      {
                        'border-start': hasCategories && !!index && !hasCategoryBorder,
                        'category-border': hasCategoryBorder,
                      }
                    )}
                    key={key}
                    aria-sort={sortColumn === index ? sortOrder : defaultSortOrder}
                    style={{
                      background: columnHeaderBackgrounds[index] || undefined,
                      padding: isSmall ? '0.3rem' : undefined,
                    }}
                  >
                    <button
                      className="sort-btn py-0 ps-0 pe-1_25 pos-r border-0 text-gray-darkest fw-bold"
                      onClick={onColumnSort}
                      data-index={index}
                    >
                      {headerText}
                      <i className="arrow icon-arrow-up5" aria-hidden />
                      <i className="arrow icon-arrow-down5" aria-hidden />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {vehicleEditorialRankings.map((columnValues, index) => {
              const key = `${entry.id}-row-${index}`;

              return (
                <tr key={key}>
                  {columnValues.map((columnValue, columnIndex) => {
                    const columnKey = `${key}-column-${columnIndex}`;
                    const hasAlignment = POSSIBLE_ALIGN.includes(columnsAlign[columnIndex]);
                    const hasCategoryBorder = !!columnIndex && categoriesStartIndexes.includes(columnIndex);

                    return (
                      <ContentFragment
                        componentToUse="td"
                        classes={classnames({
                          [`text-${bootstrapMigrationLeftRightToStartEnd(columnsAlign[columnIndex])}`]: hasAlignment,
                          [`align-${verticalAlign}`]: hasVerticalAlignment,
                          'border-start': hasCategories && !!columnIndex && !hasCategoryBorder,
                          'category-border': hasCategoryBorder,
                        })}
                        inlineStyle={isSmall ? { padding: '0.3rem' } : null}
                        key={columnKey}
                      >
                        {columnValue}
                      </ContentFragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </section>
    </article>
  );
}

EvRangeTableUI.propTypes = {
  entry: CmsEntities.Content,
  className: PropTypes.string,
  shareOptions: PropTypes.shape({}),
  vehicleEditorialRankings: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
};

export const EvRangeTable = connectToModel(EvRangeTableUI, {
  vehicleEditorialRankings: bindToPath(
    ({ type }) => buildTypeRankingsPath(type),
    TypeRankingsModel,
    transformToRowValues
  ),
});
